// P5 (D98, D101): a row's one button, its ⋯, and Move to topic's dialog.
//
// Draft an article is a row's one button wherever the row offers it; every other action waits in its ⋯
// (Check my results' rows have no button: Edit and Remove target sit in the ⋯). Actions no row draws any
// more (MENU_HIDDEN_ACTIONS: Change stage, D101) are left out wherever a table still sends them.
//
// The ⋯ is a disclosure of plain buttons in the Insights dropdown's keyboard contract
// (insights/dropdown.js), not role="menu" (accessibility.md §9), so it promises nothing it does not do:
// Enter, Space or Down on it open it on the first action, Up on the last; Up, Down, Home and End move
// through it; Escape, or Tab past either end, closes it with focus back on the ⋯; a press outside closes
// it. One table has one panel (activity-table/row-menu), drawn in <body> and placed against the ⋯ that
// opened it, so no scroll area round the rows cuts it off.
//
// Move to topic opens a dialog of the table's moveTopics (activity-table/move-dialog) when the table sends
// them, Move to topic's one list (MoveTopicChoices): Not relevant first, its catch-all (the searches set aside one
// at a time) first of it and its named sections after it, all scrolling as one list, then the site's topics
// under their search intent, one to a row, each with its search intent and its number of searches (plan 4.5),
// and one search's own topic marked and picked to start (P21). Find a topic filters the rows as a person types,
// a group with none left hides, and whenever no topic is named exactly what was typed, Create topic and move here
// makes the topic in the search intent picked beside it and moves the rows there in one press, with one Undo (D172). No row or bulk bar
// sets a search aside in one press (D125): Move to topic is the way into Not relevant. A table that answers Move
// to topic itself (Sort my searches' choice dialog) sends none and hears the action event as before.

import { DEFAULT_NEW_STAGE, NEW_TOPIC_STAGES, canCreateNamed, pickFirst, stepPick, topicMatches } from "../insights/topic-find.js";

import { focusOnceShown } from "../insights/focus.js";

export default function rowActionsPart({ DRAFT_ACTION, MENU_HIDDEN_ACTIONS, MOVE_ACTION, BUILT_IN_TOPIC }) {
    return {
        // What Find a topic holds and Create topic and move here's search intent, while the dialog is open (D172).
        moveQuery: "",
        moveNewStage: DEFAULT_NEW_STAGE,
        // What held focus as the dialog opened, which gets it back when it closes (returnMoveFocus()).
        moveOpener: null,

        // ------------------------------------------------------------------------- what a row offers

        // Every action the row offers, in the table's order (ActivityTable::ROW_ACTIONS).
        actionsOf(row) {
            return this.rowActionOrder.filter((action) => row.actions.includes(action) && !MENU_HIDDEN_ACTIONS.includes(action));
        },

        // The row's one button, or null when it has none.
        buttonActionOf(row) {
            return this.actionsOf(row).includes(DRAFT_ACTION) ? DRAFT_ACTION : null;
        },

        menuActionsOf(row) {
            return this.actionsOf(row).filter((action) => action !== DRAFT_ACTION);
        },

        hasMenu(row) {
            return this.menuActionsOf(row).length > 0;
        },

        actionLabel(action) {
            return this.strings.actions[action] || action;
        },

        // ------------------------------------------------------------------------------------- the ⋯

        isMenuOpen(id) {
            return this.menuFor === id;
        },

        menuPanelId() {
            return `activity-table-${this.table.id}-menu`;
        },

        // The panel's one entry while a ⋯ is open, keyed by the row and the time it opened, so a panel
        // opened from another row is drawn afresh and x-anchor reads its new ⋯ (it reads its anchor once).
        // A row that left the page drawn (a page turned, a filter), or folded, takes its panel with it.
        openMenus() {
            const row = this.menuFor === null ? null : this.visibleRows.find((candidate) => candidate.id === this.menuFor) ?? null;

            return row && !this.noticeOf(row.id) ? [{ key: `${row.id}:${this.menuOpenings}`, row }] : [];
        },

        menuTrigger() {
            return this.menuFor === null ? null : this.rowTriggerOf(this.menuFor);
        },

        rowTriggerOf(id) {
            return Array.from(this.rootElement?.querySelectorAll?.("[data-row-menu-trigger]") ?? []).find((element) => element.dataset.rowMenuFor === id) ?? null;
        },

        toggleMenu(id, fromEnd = false) {
            if (this.menuFor === id) {
                this.closeMenu(true);

                return;
            }

            this.openMenu(id, fromEnd);
        },

        // Opened from the keyboard or a press alike: focus goes to the first action, or the last for Up,
        // once the panel shows.
        openMenu(id, fromEnd = false) {
            this.menuFor = id;
            this.menuOpenings += 1;
            this.afterShow(() => {
                const items = this.menuItems();

                focusOnceShown(fromEnd ? items.at(-1) : items[0]);
            });
        },

        // Closed; focus goes back to the ⋯ when a key closed it or it held focus, never from a press
        // elsewhere, which put focus where the reader pressed.
        closeMenu(refocus = false) {
            const id = this.menuFor;

            if (id === null) {
                return;
            }

            const panel = typeof document === "undefined" ? null : document.getElementById(this.menuPanelId());
            const hadFocus = panel?.contains?.(document.activeElement) ?? false;

            this.menuFor = null;

            if (refocus || hadFocus) {
                this.rowTriggerOf(id)?.focus();
            }
        },

        // A press outside the panel closes it; a press on the ⋯ that opened it is the ⋯'s own toggle.
        pressedOutsideMenu(event) {
            if (event?.target?.closest?.("[data-row-menu-trigger]")) {
                return;
            }

            this.closeMenu(false);
        },

        menuItems() {
            const panel = typeof document === "undefined" ? null : document.getElementById(this.menuPanelId());

            return Array.from(panel?.querySelectorAll?.("[data-row-menu-item]") ?? []);
        },

        // Up and Down move through the actions, stopping at either end, as the Insights dropdown's do.
        stepMenu(delta) {
            const items = this.menuItems();
            const index = items.indexOf(document.activeElement);

            items[Math.min(items.length - 1, Math.max(0, index + delta))]?.focus();
        },

        edgeMenu(last = false) {
            const items = this.menuItems();

            (last ? items.at(-1) : items[0])?.focus();
        },

        // The panel sits at the end of <body>, so Tab past either end would leave the row far behind: it
        // closes the panel and hands focus back to the ⋯, where the reader carries on.
        menuTabOut(event) {
            const items = this.menuItems();
            const edge = event.shiftKey ? items[0] : items.at(-1);

            if (items.length > 0 && document.activeElement !== edge) {
                return;
            }

            event.preventDefault();
            this.closeMenu(true);
        },

        // A pick closes the ⋯ with focus back on it first, so focus never falls to the page while the row
        // answers; restoreFocus() then follows the row as it folds or moves.
        pickMenu(action, id) {
            this.closeMenu(true);

            return this.actOnRow(action, id);
        },

        // ----------------------------------------------------------------------- Move to topic's dialog

        hasMoveDialog() {
            return Array.isArray(this.table.moveTopics);
        },

        isMoveOpen() {
            return this.moveDialog !== null;
        },

        moveDialogId() {
            return `activity-table-${this.table.id}-move`;
        },

        // A topic's key in the dialog: the built-in Not relevant topic goes by its role, which has no id.
        moveKeyOf(topic) {
            return topic.isBuiltIn ? "not-relevant" : `topic-${topic.id}`;
        },

        // `ids` are the rows to move; `origin` is the press, which act() hands on once a topic is picked. One
        // row's own topic is listed, marked and picked to start with (P21; Ali, 14 Oct), so the reader sees
        // where the search is now and Move waits for another pick, since moving it there changes nothing.
        // Several rows have no one topic of their own, and a topic the dialog does not list marks none.
        openMoveDialog(ids, origin) {
            const own = ids.length === 1 ? this.rowById(ids[0])?.topicId ?? null : null;
            const current = typeof own === "number" && own > 0 ? (this.table.moveTopics.find((topic) => !topic.isBuiltIn && topic.id === own) ?? null) : null;

            // Read in the press that opens the dialog, so nothing inside it can have taken focus yet.
            this.moveOpener = typeof document === "undefined" ? null : document.activeElement;
            this.moveDialog = { ids, origin, current: current === null ? null : this.moveKeyOf(current) };
            this.movePick = this.moveDialog.current;
            this.moveQuery = "";
            this.moveNewStage = DEFAULT_NEW_STAGE;
            this.afterShow(() =>
                focusOnceShown(typeof document === "undefined" ? null : document.getElementById(`${this.moveDialogId()}-title`)),
            );
        },

        // Every close comes through here: Escape, Cancel, a press outside, and Move, whose row moves focus on again
        // once it answers (slim-row.js restoreFocus()). Focus goes back a tick on, after Alpine's effects have run:
        // x-trap has let go by then, and it pulls back in any focus given while it still holds.
        closeMoveDialog() {
            const opener = this.moveOpener;

            this.moveDialog = null;
            this.movePick = null;
            this.moveQuery = "";
            this.moveOpener = null;
            this.$nextTick(() => this.returnMoveFocus(opener));
        },

        // x-trap's own return is off (.noreturn, activity-table/move-dialog). It returns focus to whatever held it
        // 15ms after the dialog opened, and the title, focused a frame after opening, can take focus first: Escape
        // would then send focus to the hidden title, which the browser drops to <body> (accessibility.md §10).
        // Focus goes back only from inside the dialog or <body>; anywhere else, the reader already moved it on.
        returnMoveFocus(opener) {
            if (!opener?.isConnected) {
                return;
            }

            const focused = document.activeElement;

            if (focused && focused !== document.body && !document.getElementById(this.moveDialogId())?.contains(focused)) {
                return;
            }

            opener.focus();
        },

        // What Find a topic reads of a topic: its name, and the catch-all's group too, so "not relevant" finds it.
        moveFindText(topic) {
            return topic.isBuiltIn ? `${topic.name} ${this.strings.move.groups.not_relevant ?? ""}` : topic.name;
        },

        // The topics Find a topic leaves showing, in the table's order (ActivityTable::MOVE_GROUPS).
        moveTopicsShown() {
            return this.hasMoveDialog() ? this.table.moveTopics.filter((topic) => topicMatches(this.moveFindText(topic), this.moveQuery)) : [];
        },

        // The dialog's groups (ActivityTable::MOVE_GROUPS order, as the table sent its topics: Not relevant's
        // catch-all first of its group), each with its topics that show and the line under its name, the count of
        // those topics; a group with none is left out. Each topic carries its own line (moveNoteOf()) and, for the
        // topic the search is in now, the words that say so.
        moveGroups() {
            if (!this.hasMoveDialog() || this.moveDialog === null) {
                return [];
            }

            const words = this.strings.move;
            const groups = [];

            this.moveTopicsShown().forEach((topic) => {
                let group = groups.find((candidate) => candidate.key === topic.group);

                if (!group) {
                    group = { key: topic.group, name: words.groups[topic.group] ?? topic.group, topics: [] };
                    groups.push(group);
                }

                const key = this.moveKeyOf(topic);

                group.topics.push({ ...topic, key, note: this.moveNoteOf(topic), current: key === this.moveDialog.current ? words.current : null });
            });

            groups.forEach((group) => {
                group.note = group.key === "not_relevant" ? words.notRelevantNote : (group.topics.length === 1 ? words.topicsOne : words.topicsMany).replace(":count", group.topics.length);
            });

            return groups;
        },

        // Something typed in Find a topic: the pick goes back to where it started, so Move never moves into a
        // topic the filter has hidden (sketch 6).
        moveFound() {
            this.movePick = this.moveDialog?.current ?? null;
        },

        // Down or Enter in Find a topic: the first topic that shows takes the pick and focus.
        moveFindFirst() {
            pickFirst(typeof document === "undefined" ? null : document.getElementById(this.moveDialogId()));
        },

        // Up and Down (and Left and Right) on a row: the next or previous topic that shows takes the pick and focus.
        moveStep(delta) {
            stepPick(typeof document === "undefined" ? null : document.getElementById(this.moveDialogId()), delta);
        },

        // No topic is named exactly what Find a topic holds, whatever else matches part of it: the dialog offers Create
        // topic and move here (D172). The catch-all answers to its group's name too.
        moveCanCreate() {
            if (!this.hasMoveDialog()) {
                return false;
            }

            const names = this.table.moveTopics.flatMap((topic) => (topic.isBuiltIn ? [topic.name, this.strings.move.groups.not_relevant] : [topic.name]));

            return canCreateNamed(names, this.moveQuery);
        },

        moveNoMatchWords() {
            return this.moveCanCreate() ? (this.strings.move.noMatch ?? "").replace(":query", this.moveQuery.trim()) : "";
        },

        // The search intents a new topic may take, as the Insights dropdown lists its choices.
        moveNewStages() {
            return NEW_TOPIC_STAGES.map((stage) => ({ value: stage, label: this.strings.move.groups[stage] ?? stage }));
        },

        // Create topic and move here (D172): the topic typed, in the search intent picked, takes the rows, as a pick
        // moves them (confirmMove()), its {name, stage} travelling as the host call's value
        // (MoveTopicChoices::newTopicOf(), InsightsWriter::newTopic()).
        confirmCreateMove() {
            if (this.moveDialog === null || !this.moveCanCreate()) {
                return;
            }

            const { ids, origin } = this.moveDialog;
            const value = { name: this.moveQuery.trim(), stage: NEW_TOPIC_STAGES.includes(this.moveNewStage) ? this.moveNewStage : DEFAULT_NEW_STAGE };

            this.closeMoveDialog();

            return this.act(MOVE_ACTION, ids, origin, value);
        },

        // The line under a topic's name (plan 4.5): its search intent, or the built-in topic's own line, then
        // how many searches it holds, as Sort my searches counts them, while the host knows the count. Without
        // one (null: not cached) it is the search intent alone, never a 0 the host did not count.
        moveNoteOf(topic) {
            const words = this.strings.move;
            const what = topic.isBuiltIn ? words.builtIn : (words.groups[topic.group] ?? topic.group);

            if (typeof topic.searches !== "number") {
                return what;
            }

            return `${what} · ${(topic.searches === 1 ? words.searchesOne : words.searchesMany).replace(":count", topic.searches.toLocaleString())}`;
        },

        moveHelp() {
            const count = this.moveDialog?.ids.length ?? 0;

            return (count === 1 ? this.strings.move.helpOne : this.strings.move.helpMany).replace(":count", count);
        },

        isMovePick(topic) {
            return this.movePick === topic.key;
        },

        pickMoveTopic(topic) {
            this.movePick = topic.key;
        },

        // Move waits for a pick, and for one other than the topic the search is in now.
        canConfirmMove() {
            return this.moveDialog !== null && this.movePick !== null && this.movePick !== this.moveDialog.current;
        },

        // The pick moves the rows, its topic travelling as the host call's value: the built-in Not relevant topic
        // by its role (BUILT_IN_TOPIC), since a site has none until a search is first set aside, every other
        // topic by its id. Into Not relevant the rows fold with their Undo; into any other topic they stay.
        confirmMove() {
            if (!this.canConfirmMove()) {
                return;
            }

            const topic = this.table.moveTopics.find((candidate) => this.moveKeyOf(candidate) === this.movePick);
            const { ids, origin } = this.moveDialog;

            this.closeMoveDialog();

            if (!topic) {
                return;
            }

            return this.act(MOVE_ACTION, ids, origin, topic.isBuiltIn ? BUILT_IN_TOPIC : topic.id);
        },
    };
}
